import { createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { CheckCircle2, Mail, Send } from "lucide-react";
import { breadcrumbSchema, webPageSchema } from "@/lib/schema";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact Pal Stars — Palworld Wiki" },
      { name: "description", content: "Contact Pal Stars for Palworld database corrections, fan art credits, guide updates and partnership questions." },
      { property: "og:title", content: "Contact Pal Stars" },
      { property: "og:description", content: "Send Palworld data corrections, credits and guide requests to Pal Stars." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/contact" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Contact Pal Stars" },
      { name: "twitter:description", content: "Send Palworld corrections, credits and guide requests." },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/contact" }],
    scripts: [breadcrumbSchema([["Home", "/"], ["Contact", "/contact"]]), webPageSchema({ name: "Contact Pal Stars", description: "Send corrections, card data fixes and feedback to the Pal Stars Palworld fan wiki team.", path: "/contact", type: "ContactPage" })],
  }),
  component: ContactPage,
});

function ContactPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState("Pal data correction");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const cleanName = name.trim();
    const cleanEmail = email.trim();
    const cleanMessage = message.trim();
    if (cleanName.length < 2 || cleanName.length > 80) { setError("Enter a valid name between 2 and 80 characters."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail) || cleanEmail.length > 255) { setError("Enter a valid email address."); return; }
    if (cleanMessage.length < 10 || cleanMessage.length > 1200) { setError("Message must be between 10 and 1200 characters."); return; }
    setError("");
    setSending(true);
    try {
      const res = await fetch("/api/public/contact", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: cleanName, email: cleanEmail, topic, message: cleanMessage }),
      });
      const body = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!res.ok || !body.ok) {
        setError(body.error ?? "We could not send your message right now. Please try again later.");
        return;
      }
      setSent(true);
      setName("");
      setEmail("");
      setMessage("");
    } catch {
      setError("Network error — please check your connection and try again.");
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-16 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
        <h1 className="mt-4 text-3xl font-bold">Message sent</h1>
        <p className="mt-2 text-muted-foreground">
          Thanks — your message is on its way to info@palstars.com and a confirmation copy is in your inbox.
        </p>
        <button onClick={() => setSent(false)} className="mt-6 rounded-lg border px-4 py-2 text-sm font-semibold hover:bg-card">
          Send another message
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
      <div className="flex items-center gap-3">
        <div className="grid h-12 w-12 place-items-center rounded-lg bg-card border"><Mail className="h-5 w-5 text-primary" /></div>
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold">Contact Us</h1>
          <p className="mt-1 text-muted-foreground">Send corrections, credits, article requests or data updates.</p>
        </div>
      </div>

      <form onSubmit={submit} className="glass rounded-xl p-5 sm:p-6 mt-8 space-y-4">
        <label className="block text-sm">
          <span className="text-muted-foreground">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} className="mt-1 w-full rounded-lg border bg-background/50 px-3 py-2 outline-none focus:ring-2 ring-primary" />
        </label>
        <label className="block text-sm">
          <span className="text-muted-foreground">Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={255} className="mt-1 w-full rounded-lg border bg-background/50 px-3 py-2 outline-none focus:ring-2 ring-primary" />
        </label>
        <label className="block text-sm">
          <span className="text-muted-foreground">Topic</span>
          <select value={topic} onChange={(e) => setTopic(e.target.value)} className="mt-1 w-full rounded-lg border bg-background/50 px-3 py-2 outline-none focus:ring-2 ring-primary">
            <option>Pal data correction</option>
            <option>Fan art credit</option>
            <option>Guide article request</option>
            <option>Broken link report</option>
            <option>Other</option>
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-muted-foreground">Message</span>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={1200} rows={7} className="mt-1 w-full rounded-lg border bg-background/50 px-3 py-2 outline-none focus:ring-2 ring-primary" />
        </label>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <button type="submit" disabled={sending} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-60">
          <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send message"}
        </button>
        
      </form>
    </div>
  );
}